import type { SupabaseClient } from '../../lib/supabase';
import type { Deal, DealSection, DealStage, Pipeline } from './types';
import { notePreview } from './types';

/** One literal per table, matching the house convention. */
const DEAL_COLUMNS =
  'id, pipeline, name, company_id, lead_id, stage_id, section_id, owner_id, next_action_date, deadline, brief_color, amount, outcome, closed_on, outcome_note, hubspot_url, hubspot_updated, in_ceo_brief, section_before_star, position, created_at';
const SECTION_COLUMNS = 'id, pipeline, name, position, holds_starred';
const STAGE_COLUMNS = 'id, pipeline, name, position';

export async function fetchDeals(client: SupabaseClient, pipeline: Pipeline): Promise<Deal[]> {
  const { data, error } = await client
    .from('deals')
    .select(DEAL_COLUMNS)
    .eq('pipeline', pipeline)
    .order('position');
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []) as unknown as Deal[];
}

/**
 * Which board one deal lives on.
 *
 * `/deals?deal=<id>` opens nothing unless the page is already showing the
 * right pipeline, because the board only loads one at a time and the drawer
 * opens by finding the id in what is loaded. Links made from inside a deal
 * carry `?pipeline=` for exactly that reason; links made from anywhere that
 * only knows a deal id cannot, and the Sales Tasks page is one of those.
 *
 * Hanneza, 15 September: "The link carries the URL parameter but it only
 * lands on the generic Deals tab list." So the page asks. One row, fetched
 * only when a link arrives without a pipeline, is cheaper than teaching every
 * caller in the app to carry one and fixes the callers that already forgot.
 */
export async function fetchDealPipeline(
  client: SupabaseClient,
  dealId: string,
): Promise<Pipeline | null> {
  const { data, error } = await client
    .from('deals')
    .select('pipeline')
    .eq('id', dealId)
    .maybeSingle();
  if (error) {
    throw new Error(error.message);
  }
  if (data === null) {
    return null;
  }
  // Anything that is not the partnerships board is the default board, the same
  // reading DealsMonitoringView applies to the URL parameter.
  return data.pipeline === 'partnerships' ? 'partnerships' : 'cg_deals';
}

export async function fetchDealSections(
  client: SupabaseClient,
  pipeline: Pipeline,
): Promise<DealSection[]> {
  const { data, error } = await client
    .from('deal_sections')
    .select(SECTION_COLUMNS)
    .eq('pipeline', pipeline)
    .order('position');
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []) as unknown as DealSection[];
}

export async function fetchDealStages(
  client: SupabaseClient,
  pipeline: Pipeline,
): Promise<DealStage[]> {
  const { data, error } = await client
    .from('deal_stages')
    .select(STAGE_COLUMNS)
    .eq('pipeline', pipeline)
    .order('position');
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []) as unknown as DealStage[];
}

/**
 * The latest comment per deal, for the board's Notes column.
 *
 * Read newest-first and kept only on first sight of each deal, so this is one
 * query rather than one per row. The board shows a preview of the real thread
 * — there is no notes field to fall out of step with it.
 */
export async function fetchLatestDealNotes(
  client: SupabaseClient,
  dealIds: string[],
): Promise<Map<string, string>> {
  const notes = new Map<string, string>();
  if (dealIds.length === 0) {
    return notes;
  }
  const { data, error } = await client
    .from('task_comments')
    .select('deal_id, body, created_at')
    .in('deal_id', dealIds)
    .order('created_at', { ascending: false });
  if (error) {
    throw new Error(error.message);
  }
  for (const row of (data ?? []) as unknown as { deal_id: string; body: string }[]) {
    if (!notes.has(row.deal_id)) {
      notes.set(row.deal_id, notePreview(row.body));
    }
  }
  return notes;
}

/**
 * A new deal starts at the FIRST stage of its pipeline, never at no stage.
 *
 * Kyu, 31 Aug 2026: "remove 'No Stage' stage. Deal starts with 'In Talks'."
 * A deal nobody has staged is not a different kind of deal — it is a deal at
 * the beginning, and leaving stage_id null made the Kanban grow a No stage
 * column for deals that simply had not been told where they were.
 *
 * `promote_lead_to_deal` has always done this in the database. Deals added
 * from the board did not, which is the whole reason the null ever existed.
 * The lookup is a read of a five-row table, and it FALLS BACK to null rather
 * than failing: a pipeline with no stages configured must still be able to
 * take a deal.
 */
export async function createDeal(
  client: SupabaseClient,
  input: {
    pipeline: Pipeline;
    name: string;
    sectionId: string | null;
    position: number;
    /**
     * Who the deal is for. Both null only for somebody without Outbound
     * permission, who cannot be offered a lead list to pick from — see
     * AddDealRow.tsx. Everyone else answers one or the other, which is what
     * stops this path producing the orphans it produced all through August.
     */
    leadId: string | null;
    companyId: string | null;
  },
  createdBy: string,
): Promise<Deal> {
  const { data: firstStage } = await client
    .from('deal_stages')
    .select('id')
    .eq('pipeline', input.pipeline)
    .order('position')
    .limit(1)
    .maybeSingle();

  const { data, error } = await client
    .from('deals')
    .insert({
      pipeline: input.pipeline,
      name: input.name,
      section_id: input.sectionId,
      stage_id: firstStage?.id ?? null,
      position: input.position,
      lead_id: input.leadId,
      company_id: input.companyId,
      created_by: createdBy,
    })
    .select(DEAL_COLUMNS)
    .single();
  if (error) {
    throw new Error(error.message);
  }
  return data as unknown as Deal;
}

export type DealUpdate = Partial<{
  name: string;
  stage_id: string | null;
  section_id: string | null;
  company_id: string | null;
  lead_id: string | null;
  owner_id: string | null;
  next_action_date: string | null;
  deadline: string | null;
  brief_color: 'red' | 'yellow' | 'green' | null;
  /** Null clears the price. See dealAmount.ts: blank is unpriced, not zero. */
  amount: number | null;
  /** Null unlinks the deal from HubSpot. See hubspotLink.ts. */
  hubspot_url: string | null;
  /** The checklist's admin tick. See dealChecklist.ts. */
  hubspot_updated: boolean;
  /** Surface this deal in the daily CEO brief. See 20270127000100. */
  in_ceo_brief: boolean;
  /** Where it sat before starring, so unstarring can put it back. */
  section_before_star: string | null;
  position: number;
}>;

/**
 * Every deal, both pipelines, for the All Deals tab.
 *
 * A separate function rather than fetchDeals with the filter dropped, so the
 * per-pipeline callers cannot accidentally lose their `.eq('pipeline', ...)`
 * and start showing the other side's board.
 */
export async function fetchAllDeals(client: SupabaseClient): Promise<Deal[]> {
  const { data, error } = await client.from('deals').select(DEAL_COLUMNS).order('name');
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []) as unknown as Deal[];
}

/** Stages for both pipelines at once, so a mixed table can name every stage. */
export async function fetchAllDealStages(client: SupabaseClient): Promise<DealStage[]> {
  const { data, error } = await client.from('deal_stages').select(STAGE_COLUMNS).order('position');
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []) as unknown as DealStage[];
}

/**
 * When each deal last heard anything. See 20270128000100.
 *
 * NULL last_activity_at is meaningful and is kept: it says we cannot tell,
 * which sorts ahead of any number of days.
 */
export async function fetchDealLastActivity(
  client: SupabaseClient,
): Promise<Map<string, string | null>> {
  const { data, error } = await client
    .from('deal_last_activity')
    .select('deal_id, last_activity_at');
  if (error) {
    throw new Error(error.message);
  }
  const rows = (data ?? []) as unknown as { deal_id: string; last_activity_at: string | null }[];
  return new Map(rows.map((row) => [row.deal_id, row.last_activity_at]));
}

export async function updateDeal(
  client: SupabaseClient,
  dealId: string,
  changes: DealUpdate,
): Promise<void> {
  const { error } = await client.from('deals').update(changes).eq('id', dealId);
  if (error) {
    throw new Error(error.message);
  }
}

export async function deleteDeal(client: SupabaseClient, dealId: string): Promise<void> {
  const { error } = await client.from('deals').delete().eq('id', dealId);
  if (error) {
    throw new Error(error.message);
  }
}

/**
 * Applies a set of section position changes. Sent in sequence so the first
 * failure stops the rest rather than leaving a half-applied order behind
 * quietly. Same shape as sections/queries.ts reorderSections for projects.
 */
export async function reorderDealSections(
  client: SupabaseClient,
  updates: { id: string; position: number }[],
): Promise<void> {
  for (const update of updates) {
    const { error } = await client
      .from('deal_sections')
      .update({ position: update.position })
      .eq('id', update.id);
    if (error) {
      throw new Error(error.message);
    }
  }
}

export async function createDealSection(
  client: SupabaseClient,
  input: { pipeline: Pipeline; name: string; position: number },
): Promise<DealSection> {
  const { data, error } = await client
    .from('deal_sections')
    .insert({ pipeline: input.pipeline, name: input.name, position: input.position })
    .select(SECTION_COLUMNS)
    .single();
  if (error) {
    throw new Error(error.message);
  }
  return data as unknown as DealSection;
}

export async function renameDealSection(
  client: SupabaseClient,
  sectionId: string,
  name: string,
): Promise<void> {
  const { error } = await client.from('deal_sections').update({ name }).eq('id', sectionId);
  if (error) {
    throw new Error(error.message);
  }
}

/**
 * Deleting a section leaves its deals alone — the foreign key nulls their
 * section rather than cascading. Removing a column should never remove the
 * accounts sitting in it.
 */
export async function deleteDealSection(client: SupabaseClient, sectionId: string): Promise<void> {
  const { error } = await client.from('deal_sections').delete().eq('id', sectionId);
  if (error) {
    throw new Error(error.message);
  }
}

export const DEAL_ERRORS: Record<string, string> = {
  deal_sections_unique_name: 'A section with that name already exists in this pipeline.',
  deal_stages_unique_name: 'A stage with that name already exists in this pipeline.',
  // The database refusing an unattached deal. Without this line the person
  // sees the raw constraint name, which reads like a bug in the Hub rather
  // than the rule it is — and the one group who can hit this (Deals without
  // Sales Tracker) is exactly the group least able to guess what it means.
  deals_must_be_attached:
    'A deal has to be for someone. Pick a lead or a company, or ask an admin for Sales Tracker access so the search can find them.',
};

/**
 * Why the board could not be LOADED — without claiming a cause nobody checked.
 *
 * ── The incident this exists because of ────────────────────────────────────
 *
 * 2 September. The deploy carrying `deals.amount` landed before the migration
 * that adds the column was run, so `amount` sat in the board's select list and
 * the list query 400'd. Everyone who opened Deals was told:
 *
 *   "Deals could not be loaded. This view is limited to the sales team."
 *
 * That sentence was hardcoded on the error branch. It named a cause — your
 * permissions — that nothing had checked, and it was wrong. The first hour of
 * the investigation went to RLS policies, which were fine the whole time.
 *
 * It is the second time this exact shape has cost a day. In August the delete
 * control was missing because a query asked for a `tasks.status` column that
 * has never existed, and that failure also surfaced as an access problem, and
 * the search also started in the wrong place.
 *
 * So: a message may only assert what it has evidence for. Permission denied is
 * the one case that gets the sales-team sentence, because it is the one case
 * where the sentence is true. Everything else says what actually happened and
 * hands over the detail, because the detail is the hour.
 */
export function dealLoadMessage(raw: string): string {
  const text = raw.toLowerCase();

  // PostgREST surfaces an RLS refusal as 42501 / "permission denied", and a
  // missing or expired token as a JWT complaint. Both are genuinely about who
  // you are, so both earn the sentence.
  if (/permission denied|row-level security|42501|jwt|not authorized|unauthorized/.test(text)) {
    return 'Deals could not be loaded. This view is limited to the sales team — ask an admin if you need access.';
  }

  // The code and the schema disagree: something the query asks for is not
  // there. 42703 is Postgres; PGRST20x is PostgREST's schema cache failing to
  // find a column or a relationship. In this Hub that almost always means a
  // migration has not been run yet, which is a five-second fix once somebody
  // is told to look at the database instead of at the policies.
  if (/does not exist|could not find the|schema cache|42703|pgrst20[0-9]/.test(text)) {
    return `Deals could not be loaded: the Hub asked the database for something it does not have. This is a bug in the Hub, not a problem with your access — usually a migration that has not been run. Show this to whoever deploys: ${raw}`;
  }

  if (/failed to fetch|networkerror|load failed|network request failed/.test(text)) {
    return 'Deals could not be loaded — the server could not be reached. Check your connection and try again.';
  }

  // Unknown, and saying so is better than guessing. The raw text travels with
  // it for the same reason.
  return raw.trim() === ''
    ? 'Deals could not be loaded. Try again, and tell an admin if it keeps happening.'
    : `Deals could not be loaded. Tell an admin what this says: ${raw}`;
}

export function dealErrorMessage(raw: string): string {
  const key = Object.keys(DEAL_ERRORS).find((code) => raw.includes(code));
  return (
    (key === undefined ? undefined : DEAL_ERRORS[key]) ?? 'That could not be saved. Try again.'
  );
}
